/**
 * Verbindungsprotokoll: was zwischen Editor und Roboter wirklich passiert.
 *
 * Anlass: die Funkverbindung verhielt sich im Betrieb unberechenbar - mal kam
 * keine Verbindung zustande, mal liess sich nichts uebertragen, mal trennte sie
 * sich von selbst. Am Schreibtisch liess sich davon nichts nachstellen, und
 * ohne die tatsaechliche Abfolge im Browser des Nutzers bleibt jede Ursache
 * geraten.
 *
 * Aufgezeichnet wird deshalb **laufend**, nicht erst auf Wunsch: ein Fehler,
 * der auftritt, bevor jemand die Aufzeichnung einschaltet, waere sonst
 * verloren.
 *
 * Zwei getrennte Ringe
 * --------------------
 * *Ereignisse* - Verbinden, Trennen, Protokollschritte, Fehler - und
 * *Datenverkehr* - jeder Schreibvorgang, jede eingehende Meldung. Getrennt,
 * weil Telemetrie mit 50 Zeilen je Sekunde sonst in Minuten jedes Ereignis
 * aus einem gemeinsamen Ring verdraengt, und genau die Ereignisse sind das,
 * was man sucht.
 *
 * Von selbst in eine Datei
 * ------------------------
 * Im Entwicklungsserver gehen die Eintraege alle zwei Sekunden an
 * ``/__verbindungsprotokoll`` und landen in ``logs/verbindungsprotokoll.log``
 * im Projektverzeichnis (vite.config.ts). Niemand muss etwas speichern.
 *
 * Telemetriezeilen und Rueckkanalbefehle werden dabei **je Sekunde
 * zusammengefasst** statt einzeln festgehalten: bei 50 Zeilen je Sekunde
 * waeren das 36 MB je Stunde, und sie sagen ueber Verbindungsprobleme wenig.
 * Alles andere - Protokollschritte, Steuerzeichen, Fehler - bleibt einzeln.
 *
 * Ueber ein Neuladen hinweg
 * -------------------------
 * Die letzten Eintraege liegen zusaetzlich im localStorage. Laedt jemand die
 * Seite neu, weil nichts mehr ging, ist die Vorgeschichte trotzdem da.
 */

import { version } from "../../environment";

export type Bereich = "funk" | "kabel" | "verbindung" | "repl" | "oberflaeche";

export interface Eintrag {
  /** Millisekunden seit Seitenstart. */
  t: number;
  /** Uhrzeit, damit sich Eintraege mit Beobachtungen am Roboter abgleichen lassen. */
  zeit: string;
  bereich: Bereich;
  was: string;
  daten?: Record<string, unknown>;
}

/** So viele Ereignisse bleiben im Speicher. */
const EREIGNISSE_MAX = 5000;
/** So viele Eintraege Datenverkehr. */
const VERKEHR_MAX = 3000;
/** Laenge der Vorschau eines Datenstuecks, in Zeichen. */
const VORSCHAU_MAX = 160;
/** Stuecke mit weniger Abstand werden zu einem Eintrag zusammengezogen. */
const ZUSAMMENZIEHEN_MS = 30;

const SPEICHER_SCHLUESSEL = "robot-verbindungsprotokoll";
/** Fuers Neuladen gesichert: die letzten Ereignisse und etwas Datenverkehr. */
const SPEICHER_EREIGNISSE = 2000;
const SPEICHER_VERKEHR = 500;
/** Hoechstens so oft wird gesichert; JSON.stringify ist nicht umsonst. */
const SPEICHER_ABSTAND_MS = 3000;
/** So oft gehen neue Eintraege an den Entwicklungsserver. */
const SENDE_ABSTAND_MS = 2000;

/** Wohin ein Entwicklungsserver das Protokoll entgegennimmt (vite.config.ts). */
export const PROTOKOLL_ENDPUNKT = "/__verbindungsprotokoll";

/** Nimmt fertige Zeilen entgegen; ``false``, wenn es nicht ging. */
export type Senke = (text: string, beimVerlassen: boolean) => Promise<boolean>;

/** Zeilen, die je Sekunde zusammengefasst statt einzeln festgehalten werden. */
const SAMMELARTEN: [string, RegExp][] = [
  ["telemetrie", /^<<T>>/],
  ["rueckkanal", /^<<C>>/],
];

/**
 * Macht ein Datenstueck lesbar: Steuerzeichen als \xNN, gekuerzt.
 *
 * Das Uploadprotokoll lebt von Steuerzeichen (Strg-A, Strg-D, \x01 als
 * Quittung). Unsichtbar waeren sie in einem Protokoll wertlos.
 */
export function vorschau(daten: Uint8Array | string): string {
  const text =
    typeof daten === "string"
      ? daten
      : new TextDecoder("utf-8", { fatal: false }).decode(daten);
  let ergebnis = "";
  for (const zeichen of text) {
    if (ergebnis.length >= VORSCHAU_MAX) {
      return ergebnis + "…";
    }
    const code = zeichen.codePointAt(0) ?? 0;
    if (zeichen === "\r") {
      ergebnis += "\\r";
    } else if (zeichen === "\n") {
      ergebnis += "\\n";
    } else if (code < 0x20 || code === 0x7f) {
      ergebnis += "\\x" + code.toString(16).padStart(2, "0");
    } else {
      ergebnis += zeichen;
    }
  }
  return ergebnis;
}

/** Fehler so, dass Name und Meldung erhalten bleiben. */
export function fehlerDaten(fehler: unknown): Record<string, unknown> {
  if (fehler instanceof Error) {
    const mitCode = fehler as Error & { code?: unknown; cause?: unknown };
    const daten: Record<string, unknown> = {
      name: fehler.name,
      meldung: fehler.message,
    };
    if (mitCode.code !== undefined) {
      daten.code = mitCode.code;
    }
    if (mitCode.cause instanceof Error) {
      daten.ursache = `${mitCode.cause.name}: ${mitCode.cause.message}`;
    }
    const mitDetail = fehler as Error & { detail?: unknown };
    if (typeof mitDetail.detail === "string") {
      // PicoReplError: was bis zur Zeitueberschreitung ankam. Oft die
      // eigentliche Auskunft.
      daten.detail = vorschau(mitDetail.detail);
    }
    return daten;
  }
  return { meldung: String(fehler) };
}

interface Gesichert {
  sitzung: string;
  ereignisse: Eintrag[];
  verkehr: Eintrag[];
}

export class Verbindungsprotokoll {
  private ereignisse: Eintrag[] = [];
  private verkehr: Eintrag[] = [];
  private vorige: Gesichert | undefined;
  private readonly sitzung = new Date().toISOString();
  private sicherungGeplant = false;
  /** Noch nicht an die Senke gegangen. */
  private ausstehend: Eintrag[] = [];
  private sendenGeplant = false;
  private senkeAus = false;
  /** Zusammengefasster Datenverkehr der laufenden Sekunde, je Art. */
  private sammlung = new Map<string, { anzahl: number; bytes: number; seit: number }>();

  constructor(
    private readonly speicher: Storage | undefined = lokalerSpeicher(),
    private readonly senke: Senke | undefined = undefined
  ) {
    this.vorige = this.laden();
    this.ereignis("oberflaeche", "sitzung beginnt", {
      version,
      browser: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      bluetooth:
        typeof navigator !== "undefined" &&
        Boolean((navigator as Navigator & { bluetooth?: unknown }).bluetooth),
    });
  }

  ereignis(bereich: Bereich, was: string, daten?: Record<string, unknown>): void {
    this.anhaengen(this.ereignisse, EREIGNISSE_MAX, bereich, was, daten);
  }

  /** Ein Datenstueck, das hinaus- oder hereinging. */
  datenverkehr(
    bereich: Bereich,
    richtung: "raus" | "rein",
    daten: Uint8Array | string,
    zusatz?: Record<string, unknown>
  ): void {
    const bytes =
      typeof daten === "string" ? new TextEncoder().encode(daten).length : daten.length;
    const text = vorschau(daten);
    for (const [art, muster] of SAMMELARTEN) {
      if (muster.test(text) && !zusatz?.fehler) {
        this.sammeln(`${bereich} ${richtung} ${art}`, bytes);
        return;
      }
    }
    this.sammlungAbschliessen(false);
    // Winzige Stuecke kurz hintereinander zu einem Eintrag. Die REPL gibt ihr
    // Echo Zeichen fuer Zeichen zurueck, jedes als eigene Funkmeldung; am
    // 14.09.2026 wurden daraus 276 000 Eintraege von je einem Byte.
    const letzter = this.verkehr[this.verkehr.length - 1];
    const jetzt = zeitpunkt();
    if (
      letzter &&
      !zusatz &&
      letzter.bereich === bereich &&
      letzter.was === richtung &&
      this.letzterVerkehrT !== undefined &&
      jetzt - this.letzterVerkehrT < ZUSAMMENZIEHEN_MS &&
      this.ausstehendEnthaelt(letzter) &&
      typeof letzter.daten?.vorschau === "string" &&
      letzter.daten.vorschau.length < VORSCHAU_MAX
    ) {
      const daten = letzter.daten;
      daten.bytes = (daten.bytes as number) + bytes;
      daten.vorschau = vorschau((daten.vorschau as string) + text);
      daten.stuecke = ((daten.stuecke as number | undefined) ?? 1) + 1;
      this.letzterVerkehrT = jetzt;
      return;
    }
    this.anhaengen(this.verkehr, VERKEHR_MAX, bereich, richtung, {
      bytes,
      vorschau: text,
      ...zusatz,
    });
    this.letzterVerkehrT = jetzt;
  }

  private letzterVerkehrT: number | undefined;

  /**
   * Nur zusammenziehen, solange der Eintrag noch nicht an die Senke ging -
   * sonst stuende in der Datei nur sein erster Teil. Ohne Senke gilt jeder
   * Eintrag als ausstehend.
   */
  private ausstehendEnthaelt(eintrag: Eintrag): boolean {
    if (!this.senke || this.senkeAus) {
      return true;
    }
    return this.ausstehend[this.ausstehend.length - 1] === eintrag;
  }

  private sammeln(schluessel: string, bytes: number): void {
    const jetzt = zeitpunkt();
    this.sammlungAbschliessen(false, jetzt);
    const eintrag = this.sammlung.get(schluessel);
    if (eintrag) {
      eintrag.anzahl += 1;
      eintrag.bytes += bytes;
    } else {
      this.sammlung.set(schluessel, { anzahl: 1, bytes, seit: jetzt });
    }
  }

  /**
   * Schreibt zusammengefassten Datenverkehr als je einen Eintrag.
   *
   * @param alle auch Sammlungen, deren Sekunde noch nicht um ist
   */
  private sammlungAbschliessen(alle: boolean, jetzt = zeitpunkt()): void {
    for (const [schluessel, eintrag] of this.sammlung) {
      if (!alle && jetzt - eintrag.seit < 1000) {
        continue;
      }
      this.sammlung.delete(schluessel);
      const [bereich, richtung, art] = schluessel.split(" ");
      this.anhaengen(this.verkehr, VERKEHR_MAX, bereich as Bereich, `${richtung} ${art}`, {
        zusammengefasst: true,
        anzahl: eintrag.anzahl,
        bytes: eintrag.bytes,
        ueber_ms: Math.round(jetzt - eintrag.seit),
      });
    }
  }

  /** Alles als Text: Kopf, dann eine JSON-Zeile je Eintrag, zeitlich sortiert. */
  alsText(): string {
    const zeilen: string[] = [
      "# Verbindungsprotokoll Lehrroboter",
      `# gespeichert ${new Date().toISOString()}`,
      `# Editor ${version}`,
      "# Eine JSON-Zeile je Eintrag. t = Millisekunden seit Seitenstart.",
    ];
    if (this.vorige) {
      zeilen.push("", `# ---- vorige Sitzung ${this.vorige.sitzung} ----`);
      for (const eintrag of sortiert(this.vorige.ereignisse, this.vorige.verkehr)) {
        zeilen.push(JSON.stringify(eintrag));
      }
    }
    zeilen.push("", `# ---- diese Sitzung ${this.sitzung} ----`);
    for (const eintrag of sortiert(this.ereignisse, this.verkehr)) {
      zeilen.push(JSON.stringify(eintrag));
    }
    return zeilen.join("\n") + "\n";
  }

  /** Bietet das Protokoll als Datei zum Speichern an. */
  herunterladen(): void {
    const blob = new Blob([this.alsText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `verbindungsprotokoll-${this.sitzung.replace(/[:.]/g, "-")}.log`;
    link.click();
    URL.revokeObjectURL(url);
    this.ereignis("oberflaeche", "protokoll gespeichert");
  }

  anzahl(): { ereignisse: number; verkehr: number } {
    return { ereignisse: this.ereignisse.length, verkehr: this.verkehr.length };
  }

  private anhaengen(
    ring: Eintrag[],
    hoechstens: number,
    bereich: Bereich,
    was: string,
    daten?: Record<string, unknown>
  ): void {
    const eintrag: Eintrag = {
      t: Math.round(zeitpunkt() * 10) / 10,
      zeit: new Date().toISOString(),
      bereich,
      was,
      ...(daten ? { daten } : {}),
    };
    ring.push(eintrag);
    if (ring.length > hoechstens) {
      ring.splice(0, ring.length - hoechstens);
    }
    this.sicherungPlanen();
    if (this.senke && !this.senkeAus) {
      if (this.ausstehend.length === 0) {
        this.ausstehend.push(this.kopfeintrag());
      }
      this.ausstehend.push(eintrag);
      this.sendenPlanen();
    }
  }

  private gesendeterKopf = false;

  /** Einmal je Sitzung vorneweg, damit die Datei Sitzungen trennt. */
  private kopfeintrag(): Eintrag {
    if (this.gesendeterKopf) {
      return {
        t: Math.round(zeitpunkt() * 10) / 10,
        zeit: new Date().toISOString(),
        bereich: "oberflaeche",
        was: "fortsetzung",
        daten: { sitzung: this.sitzung },
      };
    }
    this.gesendeterKopf = true;
    return {
      t: 0,
      zeit: this.sitzung,
      bereich: "oberflaeche",
      was: "==== neue sitzung ====",
      daten: { sitzung: this.sitzung, editor: version },
    };
  }

  private sendenPlanen(): void {
    if (this.sendenGeplant) {
      return;
    }
    this.sendenGeplant = true;
    setTimeout(() => {
      this.sendenGeplant = false;
      void this.senden(false);
    }, SENDE_ABSTAND_MS);
  }

  /** Gibt ausstehende Eintraege an die Senke. */
  async senden(beimVerlassen: boolean): Promise<void> {
    if (!this.senke || this.senkeAus) {
      return;
    }
    this.sammlungAbschliessen(beimVerlassen);
    if (this.ausstehend.length === 0) {
      return;
    }
    const paket = this.ausstehend;
    this.ausstehend = [];
    const text = paket.map((e) => JSON.stringify(e)).join("\n") + "\n";
    const ok = await this.senke(text, beimVerlassen).catch(() => false);
    if (!ok) {
      // Kein Entwicklungsserver mit Endpunkt - etwa in der gebauten Fassung.
      // Dann bleibt es beim Protokoll im Speicher und im localStorage, und es
      // wird nicht bei jedem Eintrag erneut ein Netzwerkfehler erzeugt.
      this.senkeAus = true;
    }
  }

  private sicherungPlanen(): void {
    if (this.sicherungGeplant || !this.speicher) {
      return;
    }
    this.sicherungGeplant = true;
    setTimeout(() => {
      this.sicherungGeplant = false;
      this.sichern();
    }, SPEICHER_ABSTAND_MS);
  }

  /** Legt die juengsten Eintraege im localStorage ab. Scheitert still. */
  sichern(): void {
    if (!this.speicher) {
      return;
    }
    const gesichert: Gesichert = {
      sitzung: this.sitzung,
      ereignisse: this.ereignisse.slice(-SPEICHER_EREIGNISSE),
      verkehr: this.verkehr.slice(-SPEICHER_VERKEHR),
    };
    try {
      this.speicher.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(gesichert));
    } catch {
      // Voll oder gesperrt: dann eben ohne Vorgeschichte. Das Protokoll im
      // Speicher bleibt davon unberuehrt.
    }
  }

  private laden(): Gesichert | undefined {
    if (!this.speicher) {
      return undefined;
    }
    try {
      const roh = this.speicher.getItem(SPEICHER_SCHLUESSEL);
      if (!roh) {
        return undefined;
      }
      const gesichert = JSON.parse(roh) as Gesichert;
      if (!Array.isArray(gesichert.ereignisse) || !Array.isArray(gesichert.verkehr)) {
        return undefined;
      }
      return gesichert;
    } catch {
      return undefined;
    }
  }
}

function zeitpunkt(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Schickt an den Entwicklungsserver; beim Verlassen der Seite per Beacon. */
const entwicklungsserver: Senke = async (text, beimVerlassen) => {
  if (beimVerlassen && typeof navigator !== "undefined" && navigator.sendBeacon) {
    return navigator.sendBeacon(PROTOKOLL_ENDPUNKT, text);
  }
  const antwort = await fetch(PROTOKOLL_ENDPUNKT, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: text,
  });
  return antwort.ok;
};

function sortiert(a: Eintrag[], b: Eintrag[]): Eintrag[] {
  return [...a, ...b].sort((x, y) => x.t - y.t);
}

function lokalerSpeicher(): Storage | undefined {
  try {
    return typeof localStorage !== "undefined" ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Das eine Protokoll dieser Seite.
 *
 * Mit Senke nur im Entwicklungsserver; in Pruefungen und in der gebauten
 * Fassung ohne.
 */
export const protokoll = new Verbindungsprotokoll(
  lokalerSpeicher(),
  import.meta.env.DEV && import.meta.env.MODE !== "test" && typeof window !== "undefined"
    ? entwicklungsserver
    : undefined
);

if (typeof window !== "undefined") {
  // Beim Verlassen noch einmal sichern und senden: die letzten Sekunden sind
  // oft genau die, in denen es schiefging.
  window.addEventListener("pagehide", () => {
    protokoll.sichern();
    void protokoll.senden(true);
  });
}
